// src/components/UserMeet/UserMeetStage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";

import { Users, UserPlus } from "lucide-react";

import {
  useTracks,
  useParticipants,
  useLocalParticipant,
  useChat,
} from "@livekit/components-react";
import { Track } from "livekit-client";

import Icon from "../../pages/MeetingRoom/ui/Icon";
import LiveKitVideo from "../../pages/MeetingRoom/ui/LiveKitVideo";
import JungleBackground from "../../pages/MeetingRoom/ui/JungleBackground";
import PresentersRow from "../../pages/MeetingRoom/ui/PresentersRow";
import ActiveSpeakerBanner from "../../pages/MeetingRoom/ui/ActiveSpeakerBanner";
import MiniFloatingControls from "../../pages/MeetingRoom/ui/MiniFloatingControls";

import ChatModal from "../../pages/MeetingRoom/modals/ChatModal";
import FullscreenParticipantModal from "../../pages/MeetingRoom/modals/FullscreenParticipantModal";
import ParticipantsModal from "../../pages/MeetingRoom/modals/ParticipantsModal";

import ParticipantsPanel from "../../pages/MeetingRoom/panels/ParticipantsPanel";
import MeetChatPanel from "../../pages/MeetingRoom/panels/MeetChatPanel";

import MeetingRoomFeatures from "../../pages/MeetingRoom/MeetingRoomFeatures";

import { userIdFromIdentity } from "../../pages/MeetingRoom/utils/identity";
import { getRoleImageFromMetaOrApi } from "../../pages/MeetingRoom/utils/participantMeta";

import { useSeatPositions } from "../../pages/MeetingRoom/hooks/useSeatPositions";
import { useMergedParticipants } from "../../pages/MeetingRoom/hooks/useMergedParticipants";
import { useJoinLeaveToasts } from "../../pages/MeetingRoom/hooks/useJoinLeaveToasts";
import { useUnreadChat } from "../../pages/MeetingRoom/hooks/useUnreadChat";
import { usePictureInPicture } from "../../pages/MeetingRoom/hooks/usePictureInPicture";

import StageLayoutTable from "./layouts/StageLayoutTable";
import StageLayout50 from "./layouts/StageLayout50";

import "../../pages/MeetingRoom/MeetingRoom.css";
import "../../pages/UserMeet/MeetingRoom.css";
import "./layouts/UserMeetLayouts.css";

/* ----------------------------- Constants ----------------------------- */

const DEVICE_ERRORS = {
  MIC_DENIED: {
    message:
      "Microphone access denied. Click the [i] icon in your browser's address bar to allow access.",
    icon: "ℹ️",
  },
  CAMERA_DENIED: {
    message:
      "Camera access denied. Click the [i] icon in your browser's address bar to allow access.",
    icon: "ℹ️",
  },
  MIC_IN_USE: {
    message:
      "Microphone is being used by another application. Please close other apps and try again.",
    icon: "⚠️",
  },
  CAMERA_IN_USE: {
    message:
      "Camera is being used by another application. Please close other apps and try again.",
    icon: "⚠️",
  },
  MIC_NOT_FOUND: {
    message: "No microphone found. Please connect a microphone and try again.",
    icon: "🎤",
  },
  CAMERA_NOT_FOUND: {
    message: "No camera found. Please connect a camera and try again.",
    icon: "📷",
  },
};

/* ----------------------------- Screen Share Stage ----------------------------- */

function ScreenShareStage({ screenShareTrack, onStopPresenting }) {
  if (!screenShareTrack) return null;

  const { participant } = screenShareTrack;
  const participantName =
    participant?.name || participant?.identity || "Unknown";
  const isLocalPresenter = participant?.isLocal;

  return (
    <div className="jt-screenShareStage">
      <div className="jt-screenShareContainer">
        <LiveKitVideo
          trackRef={screenShareTrack}
          muted
          objectFit="contain"
          className="jt-screenShareVideo"
        />
        <div className="jt-screenShareInfo">
          <div className="jt-screenShareBadge">
            <span className="jt-screenShareIcon">
              <Icon name="screen" />
            </span>
            <span className="jt-screenShareText">
              {isLocalPresenter
                ? "You are presenting"
                : `${participantName} is presenting`}
            </span>
          </div>
          {isLocalPresenter && onStopPresenting && (
            <button
              className="jt-stopPresentingBtn"
              onClick={onStopPresenting}
              type="button"
            >
              <span className="jt-stopPresentingIcon">
                <Icon name="screenShareOff" />
              </span>
              <span className="jt-stopPresentingText">Stop presenting</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- UserMeet Info Panel ----------------------------- */

function UserMeetInfoPanel({
  meetingTitle,
  joinCode,
  participantCount,
  capacity,
  isCreator,
  creatorName,
}) {
  const [panelShow, setPanelShow] = useState(false);
  const [copiedField, setCopiedField] = useState("");

  const shareLink = `${window.location.origin}/user-meet/join/${joinCode}`;

  const roomType =
    capacity <= 4
      ? "Huddle"
      : capacity <= 6
        ? "Team Room"
        : capacity <= 10
          ? "Conference"
          : "Town Hall";

  const copyToClipboard = useCallback((text, field, label) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopiedField(""), 2000);
  }, []);

  if (panelShow) return null;

  return (
    <div className="gm-chat-shell">
      <div className="gm-chat-panel">
        {/* Header */}
        <div className="gm-chat-header">
          <div className="gm-chat-headerLeft">
            <div className="gm-chat-headerIcon">
              <i className="fa-solid fa-circle-info" />
            </div>

            <div>
              <div className="gm-chat-title">Meeting Info</div>
              <div className="gm-chat-subtitle">Session details & sharing</div>
            </div>
          </div>

          <button
            onClick={() => setPanelShow(true)}
            className="jt-mobileDrawerClose"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Content */}
        <div className="ev-panel">
          <div className="ev-card">
            {/* ── Meeting title ── */}
            <div className="ev-header">
              <h2 className="ev-title">{meetingTitle}</h2>
              <div className="ev-tag">
                <i className="fas fa-bolt" />
                <span>Live Meeting</span>
              </div>
            </div>

            {/* ── Meta items ── */}
            <div className="ev-meta">
              {/* Join Code */}
              <div className="ev-metaItem">
                <div className="ev-metaLabel">
                  <i className="fas fa-key" />
                  <span>Join Code</span>
                </div>
                <div
                  className="ev-metaValue"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <span
                    style={{ fontFamily: "monospace", letterSpacing: "1px" }}
                  >
                    {joinCode}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(joinCode, "code", "Join code")
                    }
                    className="jt-copyBtn"
                    title="Copy join code"
                    style={{
                      background: "rgba(74, 222, 128, 0.12)",
                      border: "1px solid rgba(74, 222, 128, 0.25)",
                      color: "#4ade80",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <i
                      className={`fa-solid ${
                        copiedField === "code" ? "fa-check" : "fa-copy"
                      }`}
                    />
                    {copiedField === "code" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              {/* Share Link */}
              <div className="ev-metaItem">
                <div className="ev-metaLabel">
                  <i className="fas fa-link" />
                  <span>Share Link</span>
                </div>
                <div
                  className="ev-metaValue"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      opacity: 0.85,
                    }}
                    title={shareLink}
                  >
                    {shareLink}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(shareLink, "link", "Share link")
                    }
                    className="jt-copyBtn"
                    title="Copy share link"
                    style={{
                      background: "rgba(74, 222, 128, 0.12)",
                      border: "1px solid rgba(74, 222, 128, 0.25)",
                      color: "#4ade80",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className={`fa-solid ${
                        copiedField === "link" ? "fa-check" : "fa-copy"
                      }`}
                    />
                    {copiedField === "link" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>

              {/* Room Type */}
              <div className="ev-metaItem">
                <div className="ev-metaLabel">
                  <i className="fas fa-users-viewfinder" />
                  <span>Room Type</span>
                </div>
                <div className="ev-metaValue">{roomType}</div>
              </div>

              {/* Participants */}
              <div className="ev-metaItem">
                <div className="ev-metaLabel">
                  <i className="fas fa-user-group" />
                  <span>Participants</span>
                </div>
                <div className="ev-metaValue">
                  {participantCount} / {capacity}
                  <span
                    style={{
                      marginLeft: 10,
                      fontSize: 11,
                      padding: "3px 8px",
                      borderRadius: 999,
                      background:
                        participantCount >= capacity
                          ? "rgba(248, 113, 113, 0.15)"
                          : "rgba(74, 222, 128, 0.15)",
                      color:
                        participantCount >= capacity ? "#f87171" : "#4ade80",
                      border: `1px solid ${
                        participantCount >= capacity
                          ? "rgba(248, 113, 113, 0.3)"
                          : "rgba(74, 222, 128, 0.3)"
                      }`,
                      fontWeight: 600,
                    }}
                  >
                    {participantCount >= capacity
                      ? "Full"
                      : `${capacity - participantCount} open`}
                  </span>
                </div>
              </div>

              {/* Your Role */}
              <div className="ev-metaItem">
                <div className="ev-metaLabel">
                  <i className="fas fa-user-shield" />
                  <span>Your Role</span>
                </div>
                <div className="ev-metaValue">
                  {isCreator ? "Host (Creator)" : "Participant"}
                </div>
              </div>

              {/* Host */}
              {creatorName && (
                <div className="ev-metaItem">
                  <div className="ev-metaLabel">
                    <i className="fas fa-user-tie" />
                    <span>Host</span>
                  </div>
                  <div className="ev-metaValue">{creatorName}</div>
                </div>
              )}
            </div>

            {/* ── Permissions section ── */}
            <div className="ev-description">
              <div className="ev-descriptionTitle">
                <i className="fas fa-shield-check" />
                <span>Enabled Features</span>
              </div>

              <div
                className="ev-descriptionContent"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                {[
                  { icon: "fa-microphone", label: "Audio" },
                  { icon: "fa-video", label: "Video" },
                  { icon: "fa-comments", label: "Chat" },
                  { icon: "fa-note-sticky", label: "Notes" },
                  { icon: "fa-desktop", label: "Screen share" },
                  { icon: "fa-users", label: "Breakout rooms" },
                ].map((feature) => (
                  <div
                    key={feature.label}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 12px",
                      background: "rgba(74, 222, 128, 0.06)",
                      border: "1px solid rgba(74, 222, 128, 0.15)",
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  >
                    <i
                      className={`fa-solid ${feature.icon}`}
                      style={{ color: "#4ade80", fontSize: 12 }}
                    />
                    <span>{feature.label}</span>
                    <i
                      className="fa-solid fa-check"
                      style={{
                        marginLeft: "auto",
                        color: "#4ade80",
                        fontSize: 10,
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Main UserMeet Stage ----------------------------- */

export default function UserMeetStage({
  meetingId,
  meetingTitle,
  joinCode,
  isCreator,
  connected,
  permissions,
  onLeave,
  onEndMeeting,
  deviceStatus,
  setDeviceStatus,
  capacity = 8,
  authToken,
  isBreakout = false,
  isBreakoutOwner = false,
  onInviteToBreakout = null,
  onCreateBreakout = null,
}) {
  const { localParticipant } = useLocalParticipant();
  const remoteParticipants = useParticipants();

  const isGalleryMode = capacity > 10;

  /* ── UI state ── */
  const [showParticipants, setShowParticipants] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [fsParticipant, setFsParticipant] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [showTab, setShowTab] = useState("participants");
  const [isTabHidden, setIsTabHidden] = useState(false);

  /* ── My identity ── */
  const myUserId = useMemo(
    () =>
      localStorage.getItem("userId") ||
      userIdFromIdentity(localParticipant?.identity || ""),
    [localParticipant?.identity],
  );

  const myName = useMemo(() => {
    // Prefer human-readable display name from localStorage.
    // localParticipant?.name in LiveKit is the identity string
    // (e.g. "usermeet_<id>_<timestamp>"), NOT the user's real name.
    const fromStorage =
      localStorage.getItem("fullName") ||
      localStorage.getItem("userName") ||
      localStorage.getItem("name") ||
      localStorage.getItem("displayName");

    if (fromStorage) return fromStorage;

    // Last resort: use the LiveKit name only if it doesn't look like a
    // machine-generated identity string (contains no underscores).
    const livekitName = localParticipant?.name || "";
    if (livekitName && !livekitName.includes("_")) return livekitName;

    return "Anonymous";
  }, [localParticipant?.name]);

  /* ── Tracks ── */
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );

  /* ── Merged participants (now with dynamic capacity) ── */
  const {
    screenShareTrack,
    liveOnlyParticipants,
    liveParticipantListForUI,
    participantListForModal,
    presenters,
    regularParticipants,
    activeSpeaker,
    seats,
    overflowCount,
    nameByIdentity,
  } = useMergedParticipants({
    localParticipant,
    remoteParticipants,
    tracks,
    apiUserMap: {},
    apiParticipants: [],
    tableRoster: null,
    getRoleImageFromMetaOrApi,
    capacity,
  });

  /* ── nameByUserId for MeetingRoomFeatures ── */
  const nameByUserId = useMemo(() => {
    const m = {};
    for (const p of liveOnlyParticipants) {
      const uid =
        p.userId || userIdFromIdentity(p.identity || "") || p.id || null;
      if (uid && p.name) m[String(uid)] = p.name;
    }
    return m;
  }, [liveOnlyParticipants]);

  /* ── Local camera track for PiP ── */
  const localCameraTrack = useMemo(
    () =>
      (tracks || []).find(
        (t) => t.source === Track.Source.Camera && t.participant?.isLocal,
      ),
    [tracks],
  );

  const onlineParticipantsCount = liveOnlyParticipants.length;

  /* ── Chat ── */
  const { chatMessages, send } = useChat();

  const sendChatMessage = useCallback(
    async (text) => {
      const msg = String(text || "").trim();
      if (!msg) return;
      await send(msg);
    },
    [send],
  );

  /* ── Seat positions (dynamic capacity) ── */
  const { seatPositions } = useSeatPositions(isGalleryMode ? 8 : capacity);

  /* ── Join/Leave toasts ── */
  useJoinLeaveToasts({
    connected,
    liveOnlyParticipants,
    resolvedTableName: meetingTitle,
    currentTableId: meetingId,
  });

  /* ── Unread chat ── */
  const { unreadChat, setUnreadChat } = useUnreadChat({
    chatCount: chatMessages.length,
    showTab,
  });

  /* ── Tab visibility ── */
  useEffect(() => {
    const handleVisibility = () => setIsTabHidden(document.hidden);
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  /* ── Device toggles ── */
  const toggleMic = useCallback(async () => {
    if (!localParticipant || !permissions.canPublishAudio) return;
    const next = !localParticipant.isMicrophoneEnabled;
    if (next) {
      try {
        await localParticipant.setMicrophoneEnabled(true);
      } catch (err) {
        setDeviceStatus((prev) => ({ ...prev, warningDismissed: false }));
        const config =
          err.name === "NotAllowedError"
            ? DEVICE_ERRORS.MIC_DENIED
            : err.name === "NotReadableError"
              ? DEVICE_ERRORS.MIC_IN_USE
              : err.name === "NotFoundError"
                ? DEVICE_ERRORS.MIC_NOT_FOUND
                : null;
        config
          ? toast.error(config.message, { duration: 5000, icon: config.icon })
          : toast.error("Failed to enable microphone. Please try again.");
      }
    } else {
      await localParticipant.setMicrophoneEnabled(false);
    }
  }, [localParticipant, permissions.canPublishAudio, setDeviceStatus]);

  const toggleCam = useCallback(async () => {
    if (!localParticipant || !permissions.canPublishVideo) return;
    const next = !localParticipant.isCameraEnabled;
    if (next) {
      try {
        await localParticipant.setCameraEnabled(true);
      } catch (err) {
        setDeviceStatus((prev) => ({ ...prev, warningDismissed: false }));
        const config =
          err.name === "NotAllowedError"
            ? DEVICE_ERRORS.CAMERA_DENIED
            : err.name === "NotReadableError"
              ? DEVICE_ERRORS.CAMERA_IN_USE
              : err.name === "NotFoundError"
                ? DEVICE_ERRORS.CAMERA_NOT_FOUND
                : null;
        config
          ? toast.error(config.message, { duration: 5000, icon: config.icon })
          : toast.error("Failed to enable camera. Please try again.");
      }
    } else {
      await localParticipant.setCameraEnabled(false);
    }
  }, [localParticipant, permissions.canPublishVideo, setDeviceStatus]);

  const toggleScreenShare = useCallback(async () => {
    if (!localParticipant || !permissions.canPublishScreen) return;
    try {
      await localParticipant.setScreenShareEnabled(
        !localParticipant.isScreenShareEnabled,
      );
    } catch (err) {
      if (
        err?.name === "NotAllowedError" ||
        err?.message?.includes("Permission denied") ||
        err?.message?.includes("canceled") ||
        err?.message?.includes("cancelled")
      )
        return;
      toast.error("Failed to share screen. Please try again.");
    }
  }, [localParticipant, permissions.canPublishScreen]);

  const micOn = localParticipant?.isMicrophoneEnabled ?? false;
  const camOn = localParticipant?.isCameraEnabled ?? false;
  const isSharing = localParticipant?.isScreenShareEnabled ?? false;

  /* ── PiP ── */
  usePictureInPicture({
    enabled: isTabHidden && connected,
    videoTrackRef: localCameraTrack,
    myName,
    micOn,
    camOn,
    isSharing,
    onToggleMic: toggleMic,
    onToggleCam: toggleCam,
    onToggleScreenShare: toggleScreenShare,
    onLeave,
    participantCount: onlineParticipantsCount,
  });

  /* ── Render ── */
  return (
    <MeetingRoomFeatures
      sessionId={meetingId}
      eventId={null}
      tableId={meetingId}
      authToken={authToken}
      myUserId={myUserId}
      myName={myName}
      nameByUserId={nameByUserId}
      enableToasts={true}
      showOverlays={true}
    >
      {({
        sendGlobalEmoji,
        sendPersonalLike,
        openNotes,
        EmojiQuickBar,
        updateNetworkingStatus,
        networkingStatusLoading,
      }) => (
        <div className="jt-root">
          <JungleBackground />
          <ActiveSpeakerBanner speaker={activeSpeaker} />

          {/* ── Header ── */}
          <div className="jt-header">
            <div className="jt-headerLeft">
              <span className={`jt-statusDot ${connected ? "on" : "warn"}`} />
              <div className="jt-headerTitle" title={meetingTitle}>
                {meetingTitle}
              </div>
              {connected && (
                <div className="jt-livePill">
                  <i />
                  LIVE
                </div>
              )}
              <span
                className="jt-event-name"
                style={{ fontFamily: "monospace" }}
              >
                Code: {joinCode}
              </span>
            </div>

            <div className="jt-headerRight">
              <EmojiQuickBar onSend={sendGlobalEmoji} />
            </div>
          </div>

          {/* ── Presenters / Screen share ── */}
          {!screenShareTrack && !isGalleryMode && (
            <PresentersRow
              presenters={presenters}
              onPress={(p) => setFsParticipant(p)}
            />
          )}

          {screenShareTrack && (
            <ScreenShareStage
              screenShareTrack={screenShareTrack}
              onStopPresenting={async () => {
                if (localParticipant && permissions.canPublishScreen)
                  await localParticipant.setScreenShareEnabled(false);
              }}
            />
          )}

          {/* ── Modals ── */}
          <FullscreenParticipantModal
            open={!!fsParticipant}
            participant={fsParticipant}
            onClose={() => setFsParticipant(null)}
          />

          <ParticipantsModal
            open={showParticipants}
            onClose={() => setShowParticipants(false)}
            list={participantListForModal}
          />

          <ChatModal
            open={showChat}
            onClose={() => setShowChat(false)}
            canPublishData={permissions.canPublishData}
            nameByIdentity={nameByIdentity}
            chatMessages={chatMessages}
            onSend={sendChatMessage}
          />

          {/* ── Stage + Sidebar ── */}
          <div className="jt-stage-wrp">
            {/* Render the correct stage layout based on capacity */}
            {isGalleryMode ? (
              <StageLayout50
                regularParticipants={[...presenters, ...regularParticipants]}
                activeSpeaker={activeSpeaker}
                screenShareTrack={screenShareTrack}
                onAvatarPress={(p) => setFsParticipant(p)}
                onLike={(toUserId) => sendPersonalLike(toUserId)}
              />
            ) : (
              <StageLayoutTable
                capacity={capacity}
                seats={seats}
                seatPositions={seatPositions}
                overflowCount={overflowCount}
                meetingTitle={meetingTitle}
                screenShareTrack={screenShareTrack}
                onAvatarPress={(p) => setFsParticipant(p)}
                onNamePress={null}
                onLike={(toUserId) => sendPersonalLike(toUserId)}
                onShowNetworking={null}
              />
            )}

            {/* ── Sidebar ── */}
            <div className="jt-sessions-tab">
              {/* Mobile menu button */}
              <button
                className="jt-mobileMenuBtn"
                onClick={() => setMobileMenuOpen(true)}
              >
                <i className="fa-solid fa-bars" />
              </button>

              {/* ── Mobile Drawer ── */}
              <div
                className={`jt-mobileDrawerOverlay ${mobileMenuOpen ? "open" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <div
                  className={`jt-mobileDrawer ${mobileMenuOpen ? "open" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="jt-mobileDrawerHeader">
                    <div className="jt-mobileDrawerTitle">Meeting Menu</div>
                    <button
                      className="jt-mobileDrawerClose"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        setMobilePanelOpen(true);
                      }}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      setShowTab("participants");
                      setMobileMenuOpen(false);
                      setMobilePanelOpen(true);
                    }}
                    className={`jt-mobileDrawerItem ${showTab === "participants" ? "active formob-meet" : ""}`}
                  >
                    <span className="jt-mobileDrawerIcon">
                      <Icon name="people" />
                    </span>
                    <div className="jt-mobileDrawerContent">
                      <span className="jt-mobileDrawerTitleText">
                        Participants
                      </span>
                      <span className="jt-mobileDrawerSub">
                        {onlineParticipantsCount} Online
                      </span>
                    </div>
                    <span className="jt-mobileDrawerBadge">
                      {onlineParticipantsCount}
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      setShowTab("chat");
                      setUnreadChat(0);
                      setMobileMenuOpen(false);
                      setMobilePanelOpen(true);
                    }}
                    className={`jt-mobileDrawerItem ${showTab === "chat" ? "active" : ""}`}
                  >
                    <span className="jt-mobileDrawerIcon">
                      <Icon name="chat" />
                    </span>
                    <div className="jt-mobileDrawerContent">
                      <span className="jt-mobileDrawerTitleText">Chat</span>
                      <span className="jt-mobileDrawerSub">
                        {chatMessages.length} Messages
                      </span>
                    </div>
                    {unreadChat > 0 && (
                      <span className="jt-mobileDrawerBadge jt-mobileDrawerBadgeChat">
                        {unreadChat}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      setShowTab("info");
                      setMobileMenuOpen(false);
                      setMobilePanelOpen(true);
                    }}
                    className={`jt-mobileDrawerItem ${showTab === "info" ? "active" : ""}`}
                  >
                    <span className="jt-mobileDrawerIcon">
                      <i className="fa-solid fa-info" />
                    </span>
                    <div className="jt-mobileDrawerContent">
                      <span className="jt-mobileDrawerTitleText">Info</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* ── Desktop Tab Buttons ── */}
              <div className="jt-topBtns">
                <button
                  onClick={() => setShowTab("participants")}
                  className={`jt-topBtn ${showTab === "participants" ? "active" : ""}`}
                >
                  <span className="jt-topBtnIcon">
                    <Icon name="people" />
                  </span>
                  <div className="jt-topBtnContent">
                    <span className="jt-topBtnLabel">Participants</span>
                    <span className="jt-topBtnSub">
                      {onlineParticipantsCount} Online
                    </span>
                  </div>
                  <span className="jt-topBtnBadge">
                    {onlineParticipantsCount}
                  </span>
                </button>

                <button
                  onClick={() => {
                    setShowTab("chat");
                    setUnreadChat(0);
                  }}
                  className={`jt-topBtn ${showTab === "chat" ? "active" : ""}`}
                >
                  <span className="jt-topBtnIcon">
                    <Icon name="chat" />
                  </span>
                  <div className="jt-topBtnContent">
                    <span className="jt-topBtnLabel">Chat</span>
                    <span className="jt-topBtnSub">
                      {chatMessages.length} messages
                    </span>
                  </div>
                  {unreadChat > 0 && (
                    <span className="jt-topBtnBadge jt-topBtnBadgeChat">
                      {unreadChat}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => setShowTab("info")}
                  className={`jt-topBtn ${showTab === "info" ? "active" : ""}`}
                >
                  <span className="jt-topBtnIcon">
                    <i className="fa-solid fa-info" />
                  </span>
                  <div className="jt-topBtnContent">
                    <span className="jt-topBtnLabel">Info</span>
                  </div>
                </button>
              </div>

              {/* ── Mobile Panel Content ── */}
              {mobilePanelOpen && (
                <div className="formob-meet">
                  {showTab === "participants" && (
                    <ParticipantsPanel
                      list={liveParticipantListForUI}
                      onProfileClick={null}
                    />
                  )}
                  {showTab === "chat" && (
                    <MeetChatPanel
                      canPublishData={permissions.canPublishData}
                      userNameMap={nameByIdentity}
                      chatMessages={chatMessages}
                      onMessageSend={sendChatMessage}
                    />
                  )}
                  {showTab === "info" && (
                    <UserMeetInfoPanel
                      meetingTitle={meetingTitle}
                      joinCode={joinCode}
                      participantCount={onlineParticipantsCount}
                      capacity={capacity}
                      isCreator={isCreator}
                      creatorName={null}
                    />
                  )}
                </div>
              )}

              {/* ── Desktop Panel Content ── */}
              <div className="for-desk-meet">
                {showTab === "participants" && (
                  <ParticipantsPanel
                    list={liveParticipantListForUI}
                    onProfileClick={null}
                  />
                )}
                {showTab === "chat" && (
                  <MeetChatPanel
                    canPublishData={permissions.canPublishData}
                    userNameMap={nameByIdentity}
                    chatMessages={chatMessages}
                    onMessageSend={sendChatMessage}
                  />
                )}
                {showTab === "info" && (
                  <UserMeetInfoPanel
                    meetingTitle={meetingTitle}
                    joinCode={joinCode}
                    participantCount={onlineParticipantsCount}
                    capacity={capacity}
                    isCreator={isCreator}
                    creatorName={null}
                  />
                )}
              </div>
            </div>
          </div>

          {/* ── Controls ── */}
          <div className="jt-controls">
            <div className="jt-controlsBar">
              {permissions.canPublishAudio && (
                <button
                  className={`jt-ctlBtn ${micOn ? "active" : "danger"}`}
                  type="button"
                  onClick={toggleMic}
                  title={micOn ? "Mute" : "Unmute"}
                >
                  <div className={`jt-ctlIcon ${micOn ? "active" : "danger"}`}>
                    {micOn ? <Icon name="mic" /> : <Icon name="micOff" />}
                  </div>
                  <div className="jt-ctlLabel">{micOn ? "Mute" : "Unmute"}</div>
                </button>
              )}

              {permissions.canPublishVideo && (
                <button
                  className={`jt-ctlBtn ${camOn ? "active" : "danger"}`}
                  type="button"
                  onClick={toggleCam}
                  title={camOn ? "Camera off" : "Camera on"}
                >
                  <div className={`jt-ctlIcon ${camOn ? "active" : "danger"}`}>
                    {camOn ? <Icon name="cam" /> : <Icon name="camOff" />}
                  </div>
                  <div className="jt-ctlLabel">
                    {camOn ? "Camera" : "No Cam"}
                  </div>
                </button>
              )}

              {permissions.canPublishScreen && (
                <button
                  className={`jt-ctlBtn ${isSharing ? "broadcast" : "inactive"}`}
                  type="button"
                  onClick={toggleScreenShare}
                  title={isSharing ? "Stop sharing" : "Share screen"}
                >
                  <div
                    className={`jt-ctlIcon ${isSharing ? "broadcast" : "inactive"}`}
                  >
                    <Icon name="screen" />
                  </div>
                  <div className="jt-ctlLabel">
                    {isSharing ? "Stop" : "Share"}
                  </div>
                </button>
              )}

              <button className="jt-ctlBtn" type="button" onClick={openNotes}>
                <div className="jt-ctlIcon">
                  <i className="fa-solid fa-note-sticky" />
                </div>
                <div className="jt-ctlLabel">Note</div>
              </button>

              {!isBreakout && onCreateBreakout && (
                <button
                  className="jt-ctlBtn inactive"
                  type="button"
                  onClick={onCreateBreakout}
                  title="Create Breakout Room"
                >
                  <div className="jt-ctlIcon inactive">
                    <Users size={18} />
                  </div>
                  <div className="jt-ctlLabel">Breakout</div>
                </button>
              )}

              {isBreakout && isBreakoutOwner && onInviteToBreakout && (
                <button
                  className="jt-ctlBtn inactive"
                  type="button"
                  onClick={onInviteToBreakout}
                  title="Invite More Participants"
                >
                  <div className="jt-ctlIcon inactive">
                    <UserPlus size={18} />
                  </div>
                  <div className="jt-ctlLabel">Invite</div>
                </button>
              )}

              {isCreator && !isBreakout && (
                <button
                  className="jt-ctlBtn"
                  type="button"
                  onClick={onEndMeeting}
                  title="End Meeting for Everyone"
                >
                  <div className="jt-ctlIcon danger">
                    <Icon name="close" />
                  </div>
                  <div className="jt-ctlLabel">End</div>
                </button>
              )}

              <button
                className="jt-ctlBtn"
                type="button"
                onClick={onLeave}
                title="Leave"
              >
                <div className="jt-ctlIcon leave">
                  <Icon name="leave" />
                </div>
                <div className="jt-ctlLabel leaveTxt">Leave</div>
              </button>
            </div>
          </div>

          {/* ── PiP / Mini controls ── */}
          <MiniFloatingControls
            visible={(isTabHidden || isSharing) && connected}
            localTrackRef={localCameraTrack}
            micOn={micOn}
            camOn={camOn}
            isSharing={isSharing}
            onToggleMic={toggleMic}
            onToggleCam={toggleCam}
            onToggleScreenShare={toggleScreenShare}
            onLeave={onLeave}
            canPublishAudio={permissions.canPublishAudio}
            canPublishVideo={permissions.canPublishVideo}
            canPublishScreen={permissions.canPublishScreen}
            participantCount={onlineParticipantsCount}
            myName={myName}
          />
        </div>
      )}
    </MeetingRoomFeatures>
  );
}
